// Global Instructions Rule Applied!
//
// In-app help — HTTP service for the "?" guide drawer.
//
// Talks to the Python backend only (CLAUDE.md — never Supabase directly):
//   GET /help/articles/:articleKey
//
// Guides are firm documentation, not user data, and they change about as often
// as a migration ships — so an article is fetched once and kept for the rest of
// the session in the module-level cache below. That is what keeps this off the
// global-configs payload (already ~50 cfg tables, re-pulled on every
// config-version bump) without turning the drawer into a per-open round trip.
//
// A 404 is a normal outcome, not an error: a screen may point at a guide that
// has not been written yet, and it must still work.

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../utils/endpoints.ts'
import {
  HelpArticle,
  validateHelpArticle
} from '../../state_manager/data_models/help_article_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface HelpArticleResponse {
  article: HelpArticle | null
  error?: string
}

// Session cache. `null` is cached too — a missing guide should be asked for
// once, not on every drawer open.
const cache = new Map<string, HelpArticle | null>()

export const clearHelpArticleCache = (): void => {
  cache.clear()
}

export const fetchHelpArticle = async (
  articleKey: string,
  accessToken: string | null = null
): Promise<HelpArticleResponse> => {
  if (cache.has(articleKey)) return { article: cache.get(articleKey) ?? null }

  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.HELP.ARTICLE, { articleKey }),
      { method: API_ROUTES.HELP.ARTICLE.method, headers }
    )

    if (response.status === 404) {
      cache.set(articleKey, null)
      return { article: null }
    }

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch help article: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) {
      cache.set(articleKey, null)
      return { article: null }
    }

    const article = validateHelpArticle(json.data)
    cache.set(articleKey, article)
    return { article }
  } catch (error) {
    // Deliberately NOT cached — a transient failure should be retryable by
    // reopening the drawer.
    return {
      article: null,
      error: error instanceof Error ? error.message : 'Failed to fetch help article'
    }
  }
}
